/**
 * Regular grids in EPSG:25833 (UTM 33N) metres.
 *
 * Row 0 is the northern edge, column 0 the western edge (same layout as the GeoTIFFs from
 * Kartverket's WCS). Values are cell-centred: cell (col, row) covers
 * [minX + col·cellSize, minX + (col+1)·cellSize] × [maxY − (row+1)·cellSize, maxY − row·cellSize].
 *
 * Nothing in the simulation may assume a particular cell size — always go through `cellSize`.
 */
export interface GridSpec {
  cols: number;
  rows: number;
  /** Cell edge length in metres. */
  cellSize: number;
  /** UTM easting of the western edge. */
  minX: number;
  /** UTM northing of the northern edge. */
  maxY: number;
}

/** A scalar value per cell, row-major. */
export interface ScalarField {
  spec: GridSpec;
  data: Float32Array;
}

export function cellCount(spec: GridSpec): number {
  return spec.cols * spec.rows;
}

export function gridWidth(spec: GridSpec): number {
  return spec.cols * spec.cellSize;
}

export function gridHeight(spec: GridSpec): number {
  return spec.rows * spec.cellSize;
}

export function maxX(spec: GridSpec): number {
  return spec.minX + gridWidth(spec);
}

export function minY(spec: GridSpec): number {
  return spec.maxY - gridHeight(spec);
}

export function cellCenterX(spec: GridSpec, col: number): number {
  return spec.minX + (col + 0.5) * spec.cellSize;
}

export function cellCenterY(spec: GridSpec, row: number): number {
  return spec.maxY - (row + 0.5) * spec.cellSize;
}

export function createField(spec: GridSpec, fill = 0): ScalarField {
  const data = new Float32Array(cellCount(spec));
  if (fill !== 0) data.fill(fill);
  return { spec, data };
}

/**
 * Bilinear sample at a UTM position, interpolating between cell centres. Positions outside
 * the grid clamp to the nearest edge cell.
 */
export function sampleBilinear(field: ScalarField, x: number, y: number): number {
  const { spec, data } = field;
  const fc = clamp((x - spec.minX) / spec.cellSize - 0.5, 0, spec.cols - 1);
  const fr = clamp((spec.maxY - y) / spec.cellSize - 0.5, 0, spec.rows - 1);
  const c0 = Math.floor(fc);
  const r0 = Math.floor(fr);
  const c1 = Math.min(c0 + 1, spec.cols - 1);
  const r1 = Math.min(r0 + 1, spec.rows - 1);
  const tx = fc - c0;
  const ty = fr - r0;
  const v00 = data[r0 * spec.cols + c0]!;
  const v10 = data[r0 * spec.cols + c1]!;
  const v01 = data[r1 * spec.cols + c0]!;
  const v11 = data[r1 * spec.cols + c1]!;
  return (v00 * (1 - tx) + v10 * tx) * (1 - ty) + (v01 * (1 - tx) + v11 * tx) * ty;
}

/**
 * Block-average a field onto a coarser grid covering the same extent. `coarse.cellSize` must be
 * an integer multiple of `fine.spec.cellSize` and the extents must match.
 */
export function blockAverage(fine: ScalarField, coarse: GridSpec): ScalarField {
  const k = coarse.cellSize / fine.spec.cellSize;
  if (!Number.isInteger(k) || k < 1) {
    throw new Error(
      `coarse cell size ${coarse.cellSize} is not a multiple of ${fine.spec.cellSize}`,
    );
  }
  if (coarse.cols * k !== fine.spec.cols || coarse.rows * k !== fine.spec.rows) {
    throw new Error('coarse grid extent does not match fine grid');
  }
  const out = createField(coarse);
  const fineCols = fine.spec.cols;
  const inv = 1 / (k * k);
  for (let r = 0; r < coarse.rows; r++) {
    for (let c = 0; c < coarse.cols; c++) {
      let sum = 0;
      for (let dr = 0; dr < k; dr++) {
        const rowStart = (r * k + dr) * fineCols + c * k;
        for (let dc = 0; dc < k; dc++) sum += fine.data[rowStart + dc]!;
      }
      out.data[r * coarse.cols + c] = sum * inv;
    }
  }
  return out;
}

export function clamp(v: number, lo: number, hi: number): number {
  return v < lo ? lo : v > hi ? hi : v;
}

/**
 * Expand a coarse field onto a finer grid covering the same extent (each coarse cell fills its
 * k×k block of fine cells). For categorical or bookkeeping fields where interpolation would be
 * wrong; `fine.cellSize` must divide `coarse.spec.cellSize`.
 */
export function expandBlocks(coarse: ScalarField, fine: GridSpec): ScalarField {
  const k = coarse.spec.cellSize / fine.cellSize;
  if (!Number.isInteger(k) || k < 1) {
    throw new Error(`fine cell size ${fine.cellSize} does not divide ${coarse.spec.cellSize}`);
  }
  const out = createField(fine);
  for (let r = 0; r < fine.rows; r++) {
    const cr = Math.min(coarse.spec.rows - 1, Math.floor(r / k));
    for (let c = 0; c < fine.cols; c++) {
      const cc = Math.min(coarse.spec.cols - 1, Math.floor(c / k));
      out.data[r * fine.cols + c] = coarse.data[cr * coarse.spec.cols + cc]!;
    }
  }
  return out;
}
